"use client";
import React, { FC, Fragment, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Listbox, Transition } from "@headlessui/react";
import SelectMultiple from "@/shared/SelectMultiple";
import CountryPhone from "./CountryPhone";
import { useAppSelector, useAppDispatch } from '@/app/lib/hooks';
import { RootState } from '@/app/lib/store';
import { setFormData } from '@/app/lib/reducers/recruitmentReducer';
import { getCities } from '@/app/lib/reducers/citiesReducer';
import { useTranslation, Trans } from "react-i18next";
import NcModal from "@/shared/NcModal";
import ReactHtmlParser from "react-html-parser";
import { CmsEntry, CmsData } from "@/data/types";

export interface Recruitment2Props {}

interface LanguageItem {
  id: number;
  name: string;
  nativeName: string;
  code: string;
  href: string;
  activated?: boolean;
}

interface OptionType {
  label: string;
  value: string;
}

// ----- styled field primitives (match guydeez_claude_design mockup:
// pill inputs, bold small labels). Native elements so all the existing
// name/value/onChange bindings are preserved verbatim. -----
const Field: FC<{
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ label, children, className }) => (
  <div className={className} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: "var(--g-ink-900, #1a1a1a)",
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

const pillStyle: React.CSSProperties = {
  width: "100%",
  height: 46,
  padding: "0 16px",
  borderRadius: 999,
  border: "1.5px solid var(--g-line)",
  background: "white",
  fontSize: 13,
  fontWeight: 600,
  color: "var(--g-ink-900, #1a1a1a)",
  fontFamily: "inherit",
  outline: "none",
};

// Custom styled select (Headless UI Listbox) — replaces the native
// <select> so the OPEN dropdown is fully styled (rounded panel, cream
// hover, burgundy selected + ✓) and animates smoothly. Value/onChange are
// plain strings so the existing store bindings are unchanged.
type GdOption = { value: string; label: React.ReactNode };
const GdSelect: FC<{
  value: string;
  onChange: (v: string) => void;
  options: GdOption[];
  placeholder?: string;
  capitalize?: boolean;
}> = ({ value, onChange, options, placeholder, capitalize }) => {
  const selected = options.find((o) => String(o.value) === String(value));
  const showPlaceholder =
    !selected || selected.value === "" || selected.value === "-1";
  return (
    <Listbox value={value} onChange={onChange}>
      <div style={{ position: "relative" }}>
        <Listbox.Button
          style={{
            ...pillStyle,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            cursor: "pointer",
            textAlign: "left",
            textTransform: capitalize ? "capitalize" : "none",
          }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: showPlaceholder
                ? "var(--g-ink-500)"
                : "var(--g-ink-900, #1a1a1a)",
            }}
          >
            {showPlaceholder ? placeholder : selected?.label}
          </span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--g-ink-500)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ flexShrink: 0 }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </Listbox.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-150"
          enterFrom="opacity-0 -translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 -translate-y-1"
        >
          <Listbox.Options
            style={{
              position: "absolute",
              zIndex: 30,
              top: "calc(100% + 6px)",
              left: 0,
              right: 0,
              maxHeight: 264,
              overflowY: "auto",
              padding: 6,
              margin: 0,
              listStyle: "none",
              background: "white",
              border: "1px solid var(--g-line)",
              borderRadius: 14,
              boxShadow: "var(--g-shadow-md, 0 12px 32px rgba(0,0,0,.14))",
              textTransform: capitalize ? "capitalize" : "none",
            }}
          >
            {options
              .filter((o) => o.value !== "" && o.value !== "-1")
              .map((o) => (
                <Listbox.Option key={String(o.value)} value={o.value} as={Fragment}>
                  {({ active, selected: sel }) => (
                    <li
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        padding: "9px 12px",
                        borderRadius: 9,
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: sel ? 700 : 500,
                        color: sel ? "var(--g-burgundy)" : "var(--g-ink-900, #1a1a1a)",
                        background: active ? "var(--g-cream)" : "transparent",
                        transition: "background .12s ease",
                      }}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {o.label}
                      </span>
                      {sel && (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="var(--g-burgundy)"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                          style={{ flexShrink: 0 }}
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </li>
                  )}
                </Listbox.Option>
              ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};

const Recruitment2: FC<Recruitment2Props> = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("guydeez");
  const languages = useAppSelector((state: RootState) => state.language.languages);
  const cities = useAppSelector((state: RootState) => state.cities.cities);
  const u = useAppSelector((state: RootState) => state.auth.user);
  const recruitmentForm = useAppSelector((state: RootState) => state.recruitment.formData);
  const selectedLanguage = useAppSelector((state: RootState) => state.language.selectedLanguage || "en_US");

  const options = languages.map((item: LanguageItem) => ({
    label: item.nativeName,
    value: item.id,
  }));

  const [formState, setFormState] = useState({
    lastName: "",
    firstName: "",
    gender: "",
    birthdate: "",
    phone: "",
    email: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
    city: "-1",
    language: "-1",
    bikeTour: false,
    carTour: false,
    spokenLanguages: [] as string[],
    termsAccepted: false,
  });

  const [cityQuery, setCityQuery] = useState("");
  const [showCityList, setShowCityList] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [termsEntry, setTermsEntry] = useState<CmsEntry | null>(null);
  const [termsEntryLoading, setTermsEntryLoading] = useState(false);
  const [termsEntryError, setTermsEntryError] = useState(false);
  const termsEntryUuid = "cmse-c676f5f8-aaba-44d8-8a0d-aaeed905c668";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    dispatch(setFormData(name, value));
  };

  const handleSelectMultipleChangeWithNamed = (selectedOptions: OptionType[]) => {
    const selectedValues = selectedOptions.map((option) => option.value);
    dispatch(setFormData("spokenLanguages", selectedValues));
  };

  useEffect(() => {
    dispatch(getCities());
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;
    const fetchTermsEntry = async () => {
      setTermsEntryLoading(true);
      setTermsEntryError(false);
      try {
        const response = await axios.get<CmsEntry>(
          `${process.env.NEXT_PUBLIC_SOCKET}/api/cms/entry`,
          {
            params: { content_cms_entry__: termsEntryUuid },
          }
        );
        if (isMounted) {
          setTermsEntry(response.data || null);
        }
      } catch (error) {
        if (isMounted) {
          setTermsEntry(null);
          setTermsEntryError(true);
        }
      } finally {
        if (isMounted) {
          setTermsEntryLoading(false);
        }
      }
    };
    fetchTermsEntry();
    return () => {
      isMounted = false;
    };
  }, [termsEntryUuid]);

  // Keep r.city in sync with the typed city name when it exactly matches one city.
  useEffect(() => {
    const q = (cityQuery || "").trim();
    if (q === "") {
      setFormState((prev) => ({ ...prev, city: "-1" }));
      dispatch(setFormData("city", "-1"));
      return;
    }
    const exact = (cities || []).find((c) => c.name.toLowerCase() === q.toLowerCase());
    if (exact) {
      setFormState((prev) => ({ ...prev, city: String(exact.id) }));
      dispatch(setFormData("city", String(exact.id)));
    } else {
      setFormState((prev) => ({ ...prev, city: "-1" }));
      dispatch(setFormData("city", "-1"));
    }
  }, [cityQuery, cities, dispatch]);

  // Derive selected city label if any
  const selectedCityName = (() => {
    const id = formState.city;
    if (!cities || id === "-1") return "";
    const found = cities.find((c) => String(c.id) === String(id));
    return found?.name || "";
  })();
  const termsData: CmsData | null = useMemo(() => {
    if (!termsEntry?.data?.length) return null;
    const exact = termsEntry.data.find((item) => item.language === selectedLanguage);
    if (exact) return exact;
    const fallback = termsEntry.data.find((item) => item.language === "en_US");
    return fallback || termsEntry.data[0] || null;
  }, [termsEntry, selectedLanguage]);
  const termsTitle = termsData?.title
    ? ReactHtmlParser(termsData.title)
    : t("recruitment_terms_modal_title");
  const termsContent = termsData?.content || "";

  return (
    <div className="flex flex-col" style={{ gap: 4 }}>
      {/* Header */}
      <h2
        className="serif"
        style={{
          fontSize: "clamp(20px, 3vw, 26px)",
          fontWeight: 800,
          color: "var(--g-burgundy)",
          lineHeight: 1.15,
        }}
      >
        {t("recruitment_interview_title")}
      </h2>
      <p style={{ fontSize: 13, color: "var(--g-ink-700, #4a4a4a)", lineHeight: 1.5, marginTop: 6 }}>
        {t("recruitment_thank_you_interest")}
      </p>
      <p style={{ fontSize: 13, color: "var(--g-ink-700, #4a4a4a)", lineHeight: 1.5 }}>
        {t("recruitment_innovative_way")}
      </p>
      <p style={{ fontSize: 11, fontStyle: "italic", color: "var(--g-ink-500)", marginBottom: 20, marginTop: 4 }}>
        {t("recruitment_fill_language_note")}
      </p>

      {u?.role !== 3 && u?.status !== "guide_fill_form_in_progress" ? (
        <div className="flex flex-col" style={{ gap: 16 }}>
          {/* Identity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ gap: 14 }}>
            <Field label={t("account_last_name")}>
              <input style={pillStyle} name="lastName" value={formState.lastName} onChange={handleChange} autoComplete="family-name" />
            </Field>
            <Field label={t("account_first_name")}>
              <input style={pillStyle} name="firstName" value={formState.firstName} onChange={handleChange} autoComplete="given-name" />
            </Field>
            <Field label={t("account_gender")}>
              <GdSelect
                value={formState.gender}
                placeholder={t("select_gender") as string}
                onChange={(val) => {
                  setFormState((prev) => ({ ...prev, gender: val }));
                  dispatch(setFormData("gender", val));
                }}
                options={[
                  { value: "M", label: t("account_male") },
                  { value: "F", label: t("account_female") },
                ]}
              />
            </Field>
            <Field label={t("account_date_of_birth")}>
              <input style={pillStyle} type="date" name="birthdate" value={formState.birthdate} onChange={handleChange} autoComplete="bday" />
            </Field>
          </div>

          {/* Login */}
          <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: 14 }}>
            <Field label={t("checkout_email")}>
              <input style={pillStyle} type="email" name="email" value={formState.email} onChange={handleChange} autoComplete="email" />
            </Field>
            <Field label={t("confirm_email")}>
              <input style={pillStyle} type="email" name="confirmEmail" value={formState.confirmEmail} onChange={handleChange} />
            </Field>
            <Field label={t("password")}>
              <input style={pillStyle} type="password" name="password" value={formState.password} onChange={handleChange} autoComplete="new-password" />
            </Field>
            <Field label={t("account_confirm_password")}>
              <input style={pillStyle} type="password" name="confirmPassword" value={formState.confirmPassword} onChange={handleChange} autoComplete="new-password" />
            </Field>
          </div>

          {/* Phone + city + tour types */}
          <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: 14 }}>
            <Field label={t("recruitment_phone_number_hint")}>
              <CountryPhone
                name="phone"
                value={formState.phone}
                onChange={(newValue) => {
                  setFormState((prev) => ({ ...prev, phone: newValue || "" }));
                  dispatch(setFormData("phone", newValue || ""));
                }}
              />
            </Field>
            <Field label={t("recruitment_choose_city_label")}>
              <div className="relative">
                <input
                  style={pillStyle}
                  name="cityAutocomplete"
                  value={cityQuery}
                  placeholder={t("recruitment_search_city_placeholder") as string}
                  onChange={(e) => {
                    setCityQuery(e.target.value);
                    setShowCityList(true);
                  }}
                  onFocus={() => setShowCityList(true)}
                  onBlur={() =>
                    setTimeout(() => {
                      setShowCityList(false);
                      const selected = (cities || []).find(
                        (c) => String(c.id) === String(formState.city)
                      );
                      const typed = (cityQuery || "").trim().toLowerCase();
                      const match = selected?.name?.toLowerCase() === typed;
                      if (!selected || !match) {
                        setCityQuery("");
                      }
                    }, 120)
                  }
                />
                {showCityList && (
                  <div
                    className="absolute z-10 mt-1 w-full max-h-60 overflow-auto"
                    style={{
                      background: "white",
                      border: "1px solid var(--g-line)",
                      borderRadius: 12,
                      boxShadow: "var(--g-shadow-md, 0 10px 30px rgba(0,0,0,.08))",
                    }}
                  >
                    {(cities || [])
                      .filter((c) =>
                        (cityQuery || "").trim() === ""
                          ? true
                          : c.name.toLowerCase().includes(cityQuery.toLowerCase())
                      )
                      .slice(0, 20)
                      .map((c) => (
                        <div
                          key={c.id}
                          className="capitalize"
                          style={{
                            padding: "10px 14px",
                            cursor: "pointer",
                            fontSize: 13,
                            color: "var(--g-ink-900, #1a1a1a)",
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.background = "var(--g-cream)";
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.background = "transparent";
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setFormState((prev) => ({ ...prev, city: String(c.id) }));
                            dispatch(setFormData("city", String(c.id)));
                            setCityQuery(c.name);
                            setShowCityList(false);
                          }}
                        >
                          {c.name}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </Field>
            <Field label={t("recruitment_ready_bike_tour_label")}>
              <GdSelect
                value={String(formState.bikeTour)}
                onChange={(val) => {
                  const b = val === "true";
                  setFormState((prev) => ({ ...prev, bikeTour: b }));
                  dispatch(setFormData("bikeTour", b));
                }}
                options={[
                  { value: "false", label: t("choice_no_title") },
                  { value: "true", label: t("choice_yes_title") },
                ]}
              />
            </Field>
            <Field label={t("recruitment_ready_car_tour_label")}>
              <GdSelect
                value={String(formState.carTour)}
                onChange={(val) => {
                  const b = val === "true";
                  setFormState((prev) => ({ ...prev, carTour: b }));
                  dispatch(setFormData("carTour", b));
                }}
                options={[
                  { value: "false", label: t("choice_no_title") },
                  { value: "true", label: t("choice_yes_title") },
                ]}
              />
            </Field>
          </div>

          {/* Languages */}
          <div className="grid grid-cols-1 sm:grid-cols-3" style={{ gap: 14 }}>
            <Field label={t("recruitment_native_language")}>
              <GdSelect
                capitalize
                value={formState.language}
                placeholder={t("recruitment_select_native_language") as string}
                onChange={(val) => {
                  setFormState((prev) => ({ ...prev, language: val }));
                  dispatch(setFormData("language", val));
                }}
                options={languages.map((item: LanguageItem) => ({
                  value: String(item.id),
                  label: item.nativeName,
                }))}
              />
            </Field>
            <Field label={t("recruitment_spoken_languages")} className="sm:col-span-2">
              <SelectMultiple
                name="spokenLanguages"
                onChange={handleSelectMultipleChangeWithNamed}
                options={options}
                placeholder={t("recruitment_select_languages")}
              />
            </Field>
          </div>
        </div>
      ) : (
        <p style={{ fontSize: 13, color: "var(--g-ink-700, #4a4a4a)", marginTop: 8 }}>
          {t("recruitment_already_gave_basic_info")}
        </p>
      )}

      {/* Terms */}
      <div className="flex items-start" style={{ gap: 10, marginTop: 20 }}>
        <input
          type="checkbox"
          id="recruitment-terms"
          checked={Boolean(recruitmentForm?.termsAccepted)}
          onChange={(e) => {
            const checked = e.target.checked;
            setFormState((prev) => ({ ...prev, termsAccepted: checked }));
            dispatch(setFormData("termsAccepted", checked));
          }}
          style={{
            marginTop: 2,
            width: 18,
            height: 18,
            accentColor: "var(--g-green-ada, #1F7A45)",
            cursor: "pointer",
            flexShrink: 0,
          }}
        />
        <label htmlFor="recruitment-terms" style={{ fontSize: 13, color: "var(--g-ink-700, #4a4a4a)" }}>
          <Trans
            i18nKey="recruitment_accept_terms_link"
            components={{
              terms: (
                <button
                  type="button"
                  style={{
                    color: "var(--g-green-ada, #1F7A45)",
                    textDecoration: "underline",
                    fontWeight: 700,
                    background: "transparent",
                    border: 0,
                    padding: 0,
                    cursor: "pointer",
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsTermsModalOpen(true);
                  }}
                />
              ),
            }}
          />
        </label>
      </div>

      <NcModal
        isOpenProp={isTermsModalOpen}
        onCloseModal={() => setIsTermsModalOpen(false)}
        renderTrigger={() => null}
        modalTitle={termsTitle}
        contentExtraClass="max-w-2xl"
        contentPaddingClass="p-6"
        renderContent={() => (
          <div className="text-sm text-neutral-700 dark:text-neutral-200">
            {termsEntryLoading ? (
              t("recruitment_terms_modal_body")
            ) : termsEntryError ? (
              t("recruitment_terms_modal_body")
            ) : termsContent ? (
              ReactHtmlParser(termsContent)
            ) : (
              t("recruitment_terms_modal_body")
            )}
          </div>
        )}
      />
    </div>
  );
};

export default Recruitment2;
